/* eslint-disable react/no-unescaped-entities --
 * v77.99.77: This file uses idiomatic React patterns (effect-driven
 * state hydration after fetches, English text with apostrophes /
 * quotes in JSX). The experimental React 19 hooks rules
 * (`set-state-in-effect`, `purity`, `preserve-manual-memoization`)
 * are not shipped by eslint-plugin-react-hooks 5.2.0 — only the
 * standalone Emergent lint tool surfaces them, and they mis-flag
 * these intentional patterns. `react/no-unescaped-entities` similarly
 * flags safe English text.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Search, Mail, Plus, Minus, MessageCircle, ChevronRight, HelpCircle,
  Sparkles, LifeBuoy, Bug,
} from "lucide-react";
import { BugReportDialog } from "@/components/BugReportDialog";
import { HELP } from "@/lib/helpContent";

const SUPPORT_EMAIL = "support@coparentconsole.com";
const SUPPORT_SUBJECT = "Support Request – CoParent Console";
const supportMailto = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(SUPPORT_SUBJECT)}`;


const SectionCard = ({ section, hits, openMap, toggle }) => {
  const Icon = section.icon;
  return (
    <Card
      className="border-[#D9E1E3] rounded-md shadow-none overflow-hidden"
      style={{ borderTop: "3px solid #1F9FA4" }}
      data-testid={`help-section-${section.id}`}
    >
      <div className="flex items-center gap-3 px-4 sm:px-5 py-3 border-b border-[#D9E1E3] bg-[#F4FAFA]">
        <span className="w-7 h-7 rounded-sm bg-[#E0F2F2] text-[#116B6E] inline-flex items-center justify-center flex-shrink-0">
          <Icon className="w-4 h-4" strokeWidth={1.5} />
        </span>
        <h2 className="font-heading text-base sm:text-lg font-medium text-[#2E3A3F]">{section.title}</h2>
      </div>
      <CardContent className="p-0 divide-y divide-[#D9E1E3]">
        {hits.map((it) => {
          const key = `${section.id}::${it.q}`;
          const open = !!openMap[key];
          const slug = it.q.replace(/[^\w]+/g, "-").toLowerCase();
          return (
            <div key={it.q} data-testid={`help-item-${section.id}-${slug}`}>
              <button
                type="button"
                onClick={() => toggle(key)}
                className="w-full flex items-center justify-between gap-3 px-4 sm:px-5 py-3 text-left hover:bg-[#EEF3F4] transition-colors min-h-[44px]"
                aria-expanded={open}
              >
                <span className="text-sm font-medium text-[#2E3A3F]">{it.q}</span>
                {open
                  ? <Minus className="w-4 h-4 text-[#6F828A] flex-shrink-0" strokeWidth={1.6} />
                  : <Plus className="w-4 h-4 text-[#6F828A] flex-shrink-0" strokeWidth={1.6} />}
              </button>
              {open && (
                <div
                  className="px-4 sm:px-5 pb-3 text-sm text-[#3D4D55] leading-relaxed space-y-2"
                  data-testid={`help-answer-${section.id}-${slug}`}
                >
                  <p>{it.a}</p>
                  {it.target && (
                    <Link
                      to={`${it.target.route}?spotlight=${encodeURIComponent(it.target.testid)}`}
                      className="inline-flex items-center gap-1 text-xs font-medium text-[#116B6E] hover:text-[#0F5A5C] underline-offset-2 hover:underline"
                      data-testid={`help-spotlight-${section.id}-${slug}`}
                    >
                      <Sparkles className="w-3 h-3" strokeWidth={1.8} />
                      {it.target.label || "Show me where"}
                    </Link>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default function HelpPage() {
  const [query, setQuery] = useState("");
  const [openMap, setOpenMap] = useState({});
  const [bugOpen, setBugOpen] = useState(false);

  const toggle = (key) => setOpenMap((m) => ({ ...m, [key]: !m[key] }));

  // Tokenise the query so multi-word searches still match.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return HELP.map((s) => ({ ...s, items: s.items }));
    const tokens = q.split(/\s+/).filter(Boolean);
    const matchesToken = (it, t) => (
      it.q.toLowerCase().includes(t) ||
      it.a.toLowerCase().includes(t) ||
      (it.keywords || []).some((k) => k.toLowerCase().includes(t))
    );
    return HELP
      .map((s) => ({ ...s, items: s.items.filter((it) => tokens.every((t) => matchesToken(it, t))) }))
      .filter((s) => s.items.length > 0);
  }, [query]);

  // Auto-expand all matching items when there's an active query so the
  // user can scan results without an extra tap.
  const effectiveOpenMap = useMemo(() => {
    if (!query.trim()) return openMap;
    const m = {};
    filtered.forEach((s) => s.items.forEach((it) => { m[`${s.id}::${it.q}`] = true; }));
    return m;
  }, [openMap, query, filtered]);

  const totalHits = filtered.reduce((n, s) => n + s.items.length, 0);

  // Quick-jump anchors for the table of contents
  const tocItems = HELP.map((s) => ({ id: s.id, title: s.title, icon: s.icon }));

  return (
    <div className="space-y-6 pb-8" data-testid="help-page">
      <div>
        <div className="text-xs uppercase tracking-[0.12em] text-[#6F828A]">Support</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold mt-1 inline-flex items-center gap-2">
          <HelpCircle className="w-6 h-6 text-[#1F9FA4]" strokeWidth={1.6} />
          Help & Support
        </h1>
        <p className="text-[#6F828A] mt-2 text-sm sm:text-base max-w-2xl">
          Clear answers to help you use CoParent Console with confidence. Learn how access, expenses, schedules and subscriptions work. Need help? Email us.
        </p>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6F828A]" strokeWidth={1.6} />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search Help — e.g. ‘invite’, ‘split’, ‘archived’, ‘trial’"
          className="pl-9 min-h-[44px] bg-white"
          data-testid="help-search-input"
        />
        {query && (
          <span
            className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#6F828A] tabular-nums"
            data-testid="help-search-hits"
          >
            {totalHits} match{totalHits === 1 ? "" : "es"}
          </span>
        )}
      </div>

      {/* Quick jump (only when not searching) */}
      {!query.trim() && (
        <nav
          className="bg-white border border-[#D9E1E3] rounded-md p-3 sm:p-4"
          aria-label="Help topics"
          data-testid="help-toc"
        >
          <div className="text-xs uppercase tracking-[0.1em] text-[#6F828A] mb-2 px-1">Jump to a topic</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-1.5">
            {tocItems.map(({ id, title, icon: Icon }) => (
              <a
                key={id}
                href={`#${id}`}
                className="inline-flex items-center gap-2 px-2.5 py-2 rounded-sm text-sm text-[#2E3A3F] hover:bg-[#EEF3F4] hover:text-[#116B6E] transition-colors min-h-[40px]"
                data-testid={`help-toc-${id}`}
              >
                <Icon className="w-4 h-4 text-[#1F9FA4] flex-shrink-0" strokeWidth={1.6} />
                <span className="truncate">{title}</span>
              </a>
            ))}
          </div>
        </nav>
      )}

      {/* Sections */}
      <div className="space-y-4">
        {filtered.length === 0 ? (
          <div
            className="border border-dashed border-[#D9E1E3] bg-white rounded-md py-10 px-4 text-center"
            data-testid="help-no-results"
          >
            <Search className="w-8 h-8 mx-auto text-[#6F828A]" strokeWidth={1.5} />
            <p className="mt-3 text-sm font-medium text-[#2E3A3F]">No matches for "{query}".</p>
            <p className="mt-1 text-sm text-[#6F828A]">Try a different word — or contact support below.</p>
          </div>
        ) : (
          filtered.map((s) => (
            <div key={s.id} id={s.id}>
              <SectionCard section={s} hits={s.items} openMap={effectiveOpenMap} toggle={toggle} />
            </div>
          ))
        )}
      </div>

      {/* Contact support */}
      <Card
        className="border-[#D9E1E3] rounded-md shadow-none overflow-hidden bg-gradient-to-br from-[#F4FAFA] to-[#EEF3F4]"
        style={{ borderTop: "3px solid #2E4A66" }}
        data-testid="help-contact-card"
        id="contact"
      >
        <CardContent className="p-5 sm:p-6 space-y-4">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-sm bg-white border border-[#A7C5DA] text-[#2E4A66] inline-flex items-center justify-center flex-shrink-0">
              <LifeBuoy className="w-5 h-5" strokeWidth={1.6} />
            </span>
            <div className="min-w-0">
              <h3 className="font-heading text-lg font-medium text-[#2E3A3F]">Still need a hand?</h3>
              <p className="text-sm text-[#6F828A]">
                Email <a className="underline underline-offset-2 hover:text-[#2E3A3F]" href={`mailto:${SUPPORT_EMAIL}`} data-testid="help-support-email-link">{SUPPORT_EMAIL}</a>{" "}
                and we'll get back to you as soon as possible.
              </p>
            </div>
          </div>
          <div className="text-xs text-[#6F828A] leading-relaxed">
            Helpful things to include: your account email, the device/browser you're on, the time the issue happened, and a screenshot if you have one. We'll never ask for your password.
          </div>
          <Button
            asChild
            className="bg-[#2E4A66] hover:bg-[#22384F] rounded-sm w-full sm:w-auto min-h-[44px] font-medium"
            data-testid="help-contact-support-btn"
          >
            <a href={supportMailto}>
              <MessageCircle className="w-4 h-4 mr-2" /> Contact support
              <ChevronRight className="w-4 h-4 ml-1" />
            </a>
          </Button>
        </CardContent>
      </Card>

      {/* v77.43 — Report a bug card. Same dialog the More menu uses,
          so wherever the user starts they end up at the same form. */}
      <Card
        className="border-[#D9E1E3] rounded-md shadow-none overflow-hidden bg-white"
        style={{ borderTop: "3px solid #C0291D" }}
        data-testid="help-bug-report-card"
        id="report-a-bug"
      >
        <CardContent className="p-5 sm:p-6 space-y-3">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-sm bg-[#FCE9E6] border border-[#F0BFB6] text-[#C0291D] inline-flex items-center justify-center flex-shrink-0">
              <Bug className="w-5 h-5" strokeWidth={1.6} />
            </span>
            <div className="min-w-0">
              <h3 className="font-heading text-lg font-medium text-[#2E3A3F] dark:text-[#E8EEF0]">Spotted something off?</h3>
              <p className="text-sm text-[#6F828A] dark:text-[#A8B6BB]">
                Report it directly to the team. We'll capture the page, your device, and which child you were viewing automatically — only admins see the report.
              </p>
            </div>
          </div>
          <Button
            onClick={() => setBugOpen(true)}
            className="bg-[#C0291D] hover:bg-[#8C2A22] text-white rounded-sm min-h-[44px] font-medium"
            data-testid="help-report-bug-btn"
          >
            <Bug className="w-4 h-4 mr-2" strokeWidth={1.75} /> Report a bug
          </Button>
        </CardContent>
      </Card>
      <BugReportDialog open={bugOpen} onOpenChange={setBugOpen} />

      {/* v77.58 — "Proudly Irish-made" line, mirrors the Settings
          page exactly so the brand voice is consistent across the
          app. Same low-contrast micro-copy treatment — sits at the
          bottom of the page after the bug-report card. */}
      <p
        className="text-xs text-[#A3B0B5] text-center pt-4 pb-2"
        data-testid="help-irish-ownership-line"
      >
        Proudly Irish-made and Irish-owned. Built with privacy, trust, and families in mind.
      </p>
    </div>
  );
}
